import { useQueryClient } from '@tanstack/react-query';
import { useSession } from './useSession';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type UpdateQueryData<T> = { queryKey: string[]; data: T } | { queryKey: string[]; fn: (data: any) => any };

export function useQueryInvalidator() {
  const queryClient = useQueryClient();
  const { sessionPartnerUrl } = useSession();

  return (invalidateQueryKeys: string[][]) => {
    for (const keys of invalidateQueryKeys) {
      queryClient.invalidateQueries({ queryKey: [sessionPartnerUrl, ...keys] });
    }
  };
}

export type MutationInvalidator<R> = {
  invalidateQueryKeys?: string[][];
  refreshQueryKeys?: string[][];
  data?: R;
  updateQueryData?: UpdateQueryData<R>[];
};

export function useMutationInvalidator<
  T extends (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ...args: any[]
  ) => Promise<MutationInvalidator<R>>,
  R = Awaited<ReturnType<T>>['data'],
>(fn: T) {
  const queryClient = useQueryClient();
  const { sessionPartnerUrl } = useSession();

  return async (...args: Parameters<T>): Promise<R> => {
    const { invalidateQueryKeys, refreshQueryKeys, data, updateQueryData } = await fn(...args);

    for (const keys of invalidateQueryKeys ?? []) {
      queryClient.invalidateQueries({ queryKey: [sessionPartnerUrl, ...keys] });
    }

    for (const keys of refreshQueryKeys ?? []) {
      queryClient.refetchQueries({ queryKey: [sessionPartnerUrl, ...keys] });
    }

    for (const updateKey of updateQueryData ?? []) {
      if ('data' in updateKey) {
        queryClient.setQueryData([sessionPartnerUrl, ...updateKey.queryKey], updateKey.data);
      } else {
        if (queryClient.getQueryState([sessionPartnerUrl, ...updateKey.queryKey])?.status === 'success') {
          queryClient.setQueryData([sessionPartnerUrl, ...updateKey.queryKey], updateKey.fn);
        } else {
          queryClient.invalidateQueries({ queryKey: [sessionPartnerUrl, ...updateKey.queryKey] });
        }
      }
    }

    return data as R;
  };
}
